import { cn } from "@/lib/utils";
import Logo from "@/assets/images/logo.png";

const POSITIONS = [
  { id: "SB", label: "SB", angle: 210 },
  { id: "BB", label: "BB", angle: 250 },
  { id: "UTG", label: "UTG", angle: 290 },
  { id: "UTG1", label: "UTG+1", angle: 325 },
  { id: "MP", label: "MP", angle: 0 },
  { id: "HJ", label: "HJ", angle: 35 },
  { id: "CO", label: "CO", angle: 70 },
  { id: "BTN", label: "BTN", angle: 110 },
];

const POSITION_COLORS: Record<string, string> = {
  UTG: "#ef4444",
  UTG1: "#f97316",
  MP: "#f59e0b",
  HJ: "#eab308",
  CO: "#84cc16",
  BTN: "#22c55e",
  SB: "#3b82f6",
  BB: "#8b5cf6",
};

interface PositionDiagramProps {
  highlighted: string[];
  showDealer?: boolean;
}

export const PositionDiagram = ({ highlighted, showDealer = true }: PositionDiagramProps) => {
  return (
    <div className="relative w-full max-w-[340px] mx-auto" style={{ aspectRatio: "1.5 / 1" }}>
      {/* Table felt */}
      <div className="absolute inset-[15%] rounded-[48%] bg-gradient-to-b from-emerald-800/80 to-emerald-900/80 border-[6px] border-black/80 shadow-xl" />
      <div className="absolute inset-[18%] rounded-[46%] bg-black/20 blur-xl" />

      {/* Center logo — same as game table */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img src={Logo} alt="" className="h-20 w-20 opacity-15" />
      </div>

      {/* Seats */}
      {POSITIONS.map((pos) => {
        const isActive = highlighted.length === 0 || highlighted.includes(pos.id);
        const color = POSITION_COLORS[pos.id] || "#fff";
        const rad = (pos.angle * Math.PI) / 180;
        const rx = 46;
        const ry = 42;
        const cx = 50 + rx * Math.cos(rad);
        const cy = 50 + ry * Math.sin(rad);

        return (
          <div
            key={pos.id}
            className={cn(
              "absolute flex flex-col items-center justify-center rounded-xl transition-all duration-500",
              isActive ? "scale-110" : "scale-90 opacity-30",
            )}
            style={{
              left: `${cx}%`,
              top: `${cy}%`,
              transform: `translate(-50%, -50%) ${isActive ? "scale(1.1)" : "scale(0.9)"}`,
              width: "48px",
              height: "40px",
            }}
          >
            {/* Glow behind active seats */}
            {isActive && (
              <div
                className="absolute inset-0 rounded-xl blur-md"
                style={{
                  backgroundColor: color,
                  opacity: 0.3,
                  animation: "glowPulse 2s ease-in-out infinite",
                }}
              />
            )}

            {/* Seat card */}
            <div
              className={cn(
                "relative rounded-xl px-2 py-1.5 text-center border transition-all duration-500",
                isActive
                  ? "bg-black/70 border-white/30 shadow-lg"
                  : "bg-black/40 border-white/10",
              )}
              style={isActive ? { borderColor: `${color}60` } : undefined}
            >
              <span
                className={cn(
                  "text-[11px] font-bold tracking-wide transition-colors duration-500",
                )}
                style={{ color: isActive ? color : "rgba(255,255,255,0.4)" }}
              >
                {pos.label}
              </span>
            </div>

            {/* Dealer button */}
            {showDealer && pos.id === "BTN" && isActive && (
              <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white text-black text-[8px] font-black flex items-center justify-center shadow-md border border-gray-300">
                D
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
